"use client";

import { Dialog, DialogContent } from "@cmp-src/primitives/Dialog";
import {
  WorkplaceRequestElement,
  WorkplaceRequestShareLink
} from "@gettactic/api";
import { HookFormEmailInput } from "@gettactic/components/src/form/EmailInput/HookFormEmailInput";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput/HookFormCalendarInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput/HookFormTextInput";
import { Button } from "@gettactic/components/src/primitives/button";
import { IconButton } from "@gettactic/components/src/primitives/icon-button/IconButton";
import copyToClipboard from "copy-to-clipboard";
import { Check, Copy, Link2, X } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useWorkplaceRequestShareLinkMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestShareLinkMutations";
import { useWorkplaceRequestShareLinks } from "@/features/workplace-requests/hooks/useWorkplaceRequestShareLinks";

interface ShareRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: WorkplaceRequestElement;
}

interface ShareLinkFormValues {
  name: string;
  email: string;
  expires_at?: Date;
}

/** Build the guest-facing URL for a share link on the current org origin. */
function buildShareUrl(token: string): string {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/public/requests/share/${token}`;
}

/** Human-readable expiry label for a share link. */
function formatExpiry(link: WorkplaceRequestShareLink): string {
  if (!link.expires_at) return "Never expires";
  return `Expires ${new Date(link.expires_at).toLocaleDateString()}`;
}

export const ShareRequestModal: React.FC<ShareRequestModalProps> = ({
  isOpen,
  onClose,
  request
}) => {
  const linksQuery = useWorkplaceRequestShareLinks(
    request.id,
    {},
    { enabled: isOpen }
  );
  const { createMutation, deleteMutation } =
    useWorkplaceRequestShareLinkMutations();

  const form = useForm<ShareLinkFormValues>({
    defaultValues: { name: "", email: "" }
  });

  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  const handleCreate = form.handleSubmit(async (values) => {
    try {
      await createMutation.mutateAsync({
        ticketId: request.id,
        params: {
          name: values.name.trim(),
          email: values.email.trim(),
          expires_at: values.expires_at ? values.expires_at.toISOString() : null
        }
      });
    } catch {
      // The mutation surfaces its own error toast; keep the form intact so the
      // user can retry without re-entering the guest's details.
      return;
    }
    form.reset({ name: "", email: "" });
  });

  const handleCopy = (token: string) => {
    copyToClipboard(buildShareUrl(token));
    setCopiedToken(token);
    toast.success("Share link copied");
    setTimeout(() => setCopiedToken(null), 2500);
  };

  const handleRevoke = async (linkId: string) => {
    try {
      await deleteMutation.mutateAsync({ ticketId: request.id, linkId });
    } finally {
      setPendingRevokeId(null);
    }
  };

  const links = linksQuery.data?.elements ?? [];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[90vh] max-w-lg flex-col gap-0 p-0"
        // biome-ignore lint/complexity/noUselessFragments: hide the default close button
        customCloseButton={<></>}
      >
        <div className="flex items-start justify-between border-neutral-100 border-b p-5">
          <div>
            <h2 className="font-semibold text-lg text-neutral-900">
              Share request #{request.display_id}
            </h2>
            <p className="text-neutral-700 text-sm">
              Invite a guest to view and respond without a Tactic account.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-neutral-400 hover:text-neutral-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
          <form onSubmit={handleCreate} className="flex flex-col gap-4">
            <HookFormTextInput
              form={form}
              name="name"
              label="Guest name"
              placeholder="Jane Doe"
              isRequired
            />
            <HookFormEmailInput
              form={form}
              name="email"
              label="Guest email"
              placeholder="jane@example.com"
              isRequired
            />
            <HookFormCalendarInput
              form={form}
              name="expires_at"
              label="Expires on"
              placeholder="Never expires"
            />
            <div className="flex justify-end">
              <Button type="submit" isLoading={createMutation.isPending}>
                Create link
              </Button>
            </div>
          </form>

          <div className="border-neutral-100 border-t pt-4">
            <h3 className="mb-3 font-medium text-neutral-900 text-sm">
              Active links
            </h3>

            {linksQuery.isLoading ? (
              <p className="text-neutral-500 text-sm">Loading links…</p>
            ) : links.length === 0 ? (
              <p className="text-neutral-500 text-sm">
                No share links yet. Create one above.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {links.map((link) => (
                  <li
                    key={link.id}
                    className="flex items-start gap-3 rounded-md border border-neutral-100 p-3"
                  >
                    <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-neutral-900 text-sm">
                        {link.name}
                      </div>
                      <div className="truncate text-neutral-500 text-xs">
                        {link.email} · {formatExpiry(link)}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="Copy share link"
                        onClick={() => handleCopy(link.token)}
                        className="rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700"
                        icon={
                          copiedToken === link.token ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )
                        }
                      />
                      {pendingRevokeId === link.id ? (
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPendingRevokeId(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            isLoading={deleteMutation.isPending}
                            onClick={() => handleRevoke(link.id)}
                          >
                            Revoke
                          </Button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="font-medium text-red-600 text-sm hover:text-red-700"
                          onClick={() => setPendingRevokeId(link.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
